import { useState, useEffect } from 'react'
import { supabase } from '../../supabase'
import { useAuth } from '../../contexts/AuthContext'
import { aujourdhui } from '../../utils/planning'
import TarifsJournees from './TarifsJournees'

const POLES = ['SPA', 'HYDRO', 'JOURNEES']

export default function Tarifs() {
  const { profile } = useAuth()
  const [pole, setPole] = useState('SPA')
  const [tarifs, setTarifs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ label: '', prix: '', gratuit: false, prix_reference: '' })
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [editId, setEditId] = useState(null)
  const [editForm, setEditForm] = useState({})

  useEffect(() => { load() }, [pole]) // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true)
    const { data } = await supabase
      .from('tarifs')
      .select('*')
      .eq('pole', pole)
      .order('actif', { ascending: false })
      .order('prix', { ascending: false })
    setTarifs(data || [])
    setLoading(false)
  }

  async function handleCreate(e) {
    e.preventDefault()
    setSaving(true)
    setMsg(null)

    const code = form.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').slice(0, 30) + '_' + Date.now()

    const { error } = await supabase.from('tarifs').insert({
      pole,
      code,
      label: form.label,
      prix: form.gratuit ? 0 : parseFloat(form.prix),
      prix_reference: form.gratuit && form.prix_reference ? parseFloat(form.prix_reference) : null,
      actif: true,
      date_debut: aujourdhui(),
      created_by: profile.id
    })

    if (error) {
      setMsg({ type: 'error', text: error.message })
    } else {
      setMsg({ type: 'success', text: 'Soin créé.' })
      setShowForm(false)
      setForm({ label: '', prix: '', gratuit: false, prix_reference: '' })
      load()
    }
    setSaving(false)
  }

  async function handleEdit(tarif) {
    setEditId(tarif.id)
    setEditForm({ label: tarif.label, prix: tarif.prix, gratuit: tarif.prix === 0, prix_reference: tarif.prix_reference ?? '' })
  }

  async function handleSaveEdit(id) {
    setSaving(true)
    const { error } = await supabase.from('tarifs').update({
      label: editForm.label,
      prix: editForm.gratuit ? 0 : parseFloat(editForm.prix),
      prix_reference: editForm.gratuit && editForm.prix_reference ? parseFloat(editForm.prix_reference) : null,
    }).eq('id', id)
    if (!error) { setEditId(null); load() }
    setSaving(false)
  }

  async function toggleActif(tarif) {
    // Si on désactive, on met une date_fin
    await supabase.from('tarifs').update({
      actif: !tarif.actif,
      date_fin: tarif.actif ? aujourdhui() : null
    }).eq('id', tarif.id)
    load()
  }

  const actifs = tarifs.filter(t => t.actif)
  const archives = tarifs.filter(t => !t.actif)

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Gestion des soins</h2>
          <div className="page-header-sub">Tarifs et libellés par pôle</div>
        </div>
        {pole !== 'JOURNEES' && (
          <button className="btn btn-primary" onClick={() => { setShowForm(s => !s); setMsg(null) }}>
            {showForm ? 'Annuler' : '+ Nouveau soin'}
          </button>
        )}
      </div>

      <div className="tabs" style={{marginBottom: 24}}>
        {POLES.map(p => (
          <button key={p} className={`tab-btn ${pole === p ? 'active' : ''}`} onClick={() => setPole(p)}>
            {p === 'SPA' ? 'SPA' : p === 'HYDRO' ? 'Hydrothérapie' : 'Journées'}
          </button>
        ))}
      </div>

      {pole === 'JOURNEES' && <TarifsJournees />}

      {msg && pole !== 'JOURNEES' && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      {pole !== 'JOURNEES' && showForm && (
        <div className="card" style={{marginBottom: 20}}>
          <h3 style={{marginBottom: 18}}>Nouveau soin — {pole}</h3>
          <form onSubmit={handleCreate}>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px'}}>
              <div className="form-group">
                <label>Libellé du soin</label>
                <input
                  value={form.label}
                  onChange={e => setForm(f => ({...f, label: e.target.value}))}
                  required placeholder="ex: SDE Relaxant 45€"
                />
              </div>
              <div className="form-group">
                <label>Prix (€)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.gratuit ? '0' : form.prix}
                  onChange={e => setForm(f => ({...f, prix: e.target.value}))}
                  disabled={form.gratuit}
                  placeholder="0.00"
                  required={!form.gratuit}
                />
              </div>
            </div>
            <div className="form-group" style={{flexDirection:'row', alignItems:'center', gap:10, marginBottom:20}}>
              <input
                type="checkbox" id="gratuit"
                checked={form.gratuit}
                onChange={e => setForm(f => ({...f, gratuit: e.target.checked, prix: ''}))}
              />
              <label htmlFor="gratuit" style={{textTransform:'none', letterSpacing:0, fontSize:13, color:'var(--text)'}}>
                Soin gratuit (VIP, offert…)
              </label>
            </div>
            {form.gratuit && (
              <div className="form-group" style={{marginBottom:20}}>
                <label>Valorisation pour le superviseur (€ TTC)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.prix_reference}
                  onChange={e => setForm(f => ({...f, prix_reference: e.target.value}))}
                  placeholder="ex: 29 — laisser vide si aucun impact sur la rému"
                />
                <div style={{fontSize:11, color:'var(--stone)', marginTop:4}}>
                  Séance à 0€ (hors CA) mais comptée à ce montant pour la part du superviseur (portes ouvertes).
                </div>
              </div>
            )}
            <div style={{display:'flex', gap:10}}>
              <button type="submit" className="btn btn-bordeaux" disabled={saving}>
                {saving ? 'Création...' : 'Créer le soin'}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Annuler</button>
            </div>
          </form>
        </div>
      )}

      {pole === 'JOURNEES' ? null : loading ? <div className="spinner" /> : (
        <>
          <div className="card" style={{marginBottom: 20}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
              <h3>Soins actifs</h3>
              <span className="badge badge-actif">{actifs.length} soin{actifs.length > 1 ? 's' : ''}</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Libellé</th>
                  <th className="num">Prix</th>
                  <th>Type</th>
                  <th>Depuis</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {actifs.length === 0 ? (
                  <tr><td colSpan={5} style={{textAlign:'center', color:'var(--stone)', padding:24}}>Aucun soin actif</td></tr>
                ) : actifs.map(t => (
                  <tr key={t.id}>
                    <td>
                      {editId === t.id ? (
                        <input
                          value={editForm.label}
                          onChange={e => setEditForm(f => ({...f, label: e.target.value}))}
                          style={{padding:'5px 8px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:13, width:'100%'}}
                        />
                      ) : (
                        <strong>{t.label}</strong>
                      )}
                    </td>
                    <td className="num">
                      {editId === t.id ? (
                        editForm.gratuit ? (
                          <input
                            type="number" min="0" step="0.01"
                            value={editForm.prix_reference}
                            onChange={e => setEditForm(f => ({...f, prix_reference: e.target.value}))}
                            placeholder="Valo. sup €"
                            title="Valorisation superviseur (portes ouvertes)"
                            style={{padding:'5px 8px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:13, width:90, textAlign:'right'}}
                          />
                        ) : (
                          <input
                            type="number" min="0" step="0.01"
                            value={editForm.prix}
                            onChange={e => setEditForm(f => ({...f, prix: e.target.value}))}
                            style={{padding:'5px 8px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:13, width:80, textAlign:'right'}}
                          />
                        )
                      ) : (
                        t.prix === 0
                          ? <span className="text-muted">Gratuit{t.prix_reference ? ` · sup ${t.prix_reference}€` : ''}</span>
                          : `${t.prix} €`
                      )}
                    </td>
                    <td>
                      <span className={`badge ${t.prix === 0 ? 'badge-warning' : 'badge-spa'}`}>
                        {t.prix === 0 ? 'Gratuit' : 'Payant'}
                      </span>
                    </td>
                    <td className="text-muted">{new Date(t.date_debut).toLocaleDateString('fr-FR')}</td>
                    <td style={{textAlign:'right'}}>
                      <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
                        {editId === t.id ? (
                          <>
                            <button className="btn btn-bordeaux btn-sm" onClick={() => handleSaveEdit(t.id)} disabled={saving}>
                              Sauver
                            </button>
                            <button className="btn btn-outline btn-sm" onClick={() => setEditId(null)}>
                              Annuler
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-outline btn-sm" onClick={() => handleEdit(t)}>
                              Modifier
                            </button>
                            <button className="btn btn-danger-ghost btn-sm" onClick={() => toggleActif(t)}>
                              Archiver
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {archives.length > 0 && (
            <div className="card">
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
                <h3 style={{color:'var(--stone)'}}>Soins archivés</h3>
                <span className="badge badge-inactif">{archives.length} archivé{archives.length > 1 ? 's' : ''}</span>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Libellé</th>
                    <th className="num">Prix</th>
                    <th>Archivé le</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {archives.map(t => (
                    <tr key={t.id} style={{opacity:0.6}}>
                      <td>{t.label}</td>
                      <td className="num">{t.prix === 0 ? 'Gratuit' : `${t.prix} €`}</td>
                      <td className="text-muted">{t.date_fin ? new Date(t.date_fin).toLocaleDateString('fr-FR') : '—'}</td>
                      <td style={{textAlign:'right'}}>
                        <button className="btn btn-outline btn-sm" onClick={() => toggleActif(t)}>
                          Réactiver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
